// Builder-Feld-Herkunft: reine Klassifikations-Logik, getrennt vom Component, damit sie
// ohne Browser/React unit-testbar ist (node importiert .mjs nativ). KEINE React-Imports.
//
// EHRLICHKEIT: Die Herkunft eines GEÖFFNETEN Files ist nur HEURISTISCH ableitbar (der
// Editor hat kein Onboarding-Manifest). Zuverlässig (reliable=true): SEED-KONSTANT
// (Wert == Seed), OPTIMIZER-DEFAULT (Tunable ohne Range), STRUKTUR (gepflegte Liste).
// Inferiert (reliable=false): ABGELEITET (weicht vom Seed ab in einer derive-Familie)
// und MANUELL (weicht vom Seed ab) — MANUELL vs. „vom Optimizer appliziert" sind NICHT
// sicher unterscheidbar; solche Badges werden sichtbar als Schätzung markiert.

export const BADGES = {
  derived:    { label: "Abgeleitet",        bg: "rgba(16,185,129,0.14)",  fg: "#047857", reliable: false },
  seed:       { label: "Seed-konstant",     bg: "rgba(100,116,139,0.14)", fg: "#475569", reliable: true },
  optdefault: { label: "Optimizer-Default", bg: "rgba(217,119,6,0.14)",   fg: "#b45309", reliable: true },
  manual:     { label: "Manuell",           bg: "rgba(37,99,235,0.14)",   fg: "#1d4ed8", reliable: false },
  struktur:   { label: "Struktur/Modus",    bg: "rgba(124,58,237,0.12)",  fg: "#6d28d9", reliable: true },
};

// Struktur-/Modus-Felder: feste Designwahl, NIE optimiert. EINE gepflegte Stelle
// (klar kommentiert) — kein Spiegel einer anderen Quelle, daher nicht drift-gefährdet.
export const STRUCTURE_MODE_FIELDS = new Set([
  // Instrument & Timeframe
  "inst", "name", "version", "status", "entry_tf", "ctx_tf", "regime_tf",
  // Risk-Präferenzen + §4.2-Konstanten (nicht tunable)
  "account", "risk_pct", "commission_per_contract", "spread_ticks",
  "max_daily", "cooldown", "max_bars_in_trade",
  "sl_streak_enabled", "sl_streak_threshold", "sl_streak_pause_hours", "sl_streak_reset_on_winner",
  // Stop-Loss-Struktur (Modus/Floor)
  "safety_floor_enabled", "safety_floor_atr", "safety_floor_ticks", "sl_ema_length", "sl_mode",
  // Take-Profit-Struktur/Modi (partial_preset NICHT hier — es ist optimizer-tunable,
  // s. TUNABLE_FIXED_FIELDS; Apply ersetzt take_profit.partial_pcts).
  "num_tp_levels", "tp_mode", "partial_trail_mode", "trail_after_tp",
  // Structure-Modi
  "break_confirm", "entry_mode", "direction_filter",
  // Break-Even/Trailing-Modi (Werte rr/trail sind separat = Tunable)
  "be", "be_mode", "be_offset_ticks", "be_fixed_points", "trail_ema_length",
  // Multi-TF-Gate + DTR (§4.2 invariant)
  "mtf_enabled", "mtf_daily_atr", "mtf_daily_bars", "mtf_h1_atr", "mtf_h1_bars",
  "dtr_enabled", "dtr_lookback", "dtr_percentile",
  // Filter-Toggles + Regime
  "fvg", "fvg_proximity_enabled", "ema", "ema2_length", "ema3_length",
  "regime", "regime_mode", "adx", "adx_length", "regime_atr_length", "sma_len",
  // Bad Hours + Risk-Sub
  "bad_hours", "bad_en", "max_daily_loss_pct", "profit_lock_threshold_pct", "profit_lock_factor",
]);

// derive_caps-Outputs (instrument-gemessen, aber ob DIESES File onboardet wurde =
// nur inferierbar → inferred).
export const CAPS_FIELDS = new Set(["max_contracts", "min_sl_points", "min_trade_qty"]);

// Editor-FESTWERT-Felder, die optimizer-tunable sind → werden durch Optimieren→Apply
// ersetzt. (Map auf goals.ranges-Tunables: rr1→tp1_rr, atr_min/max→sl_atr_mult, be_rr→
// be_trigger_rr, swings→*_choices, pattern_age→max_pattern_age, …). EINE gepflegte Stelle
// (Stufe 2A). Bei needs_optimize sind diese Felder Seed-Reste; Struktur/Modus + Caps NICHT.
export const TUNABLE_FIXED_FIELDS = new Set([
  "rr1", "rr2", "atr_min", "atr_max", "be_rr",
  "post_be_trail_atr_mult", "runner_trail_atr_mult",
  "entry_swing", "ctx_swing", "pattern_age", "choch_age", "partial_preset",
]);

// Die 3 datengetrieben abgeleiteten Range-Familien (derive_ranges).
export const DERIVED_RANGE_FAMILIES = new Set(["tp1_rr", "tp2_rr", "be_trigger_rr"]);

const _eq = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Editor-Feld → Herkunft. form/seedForm = parseYamlToForm(cfg) bzw. (seed.cfg).
 * @returns {{badge:keyof BADGES, inferred:boolean, why:string}}
 */
export function classifyFormField(fld, form, seedForm) {
  if (CAPS_FIELDS.has(fld))
    return { badge: "derived", inferred: true, why: "Risk-Cap aus der Messung (derive_caps) — inferiert (kein Manifest)" };
  if (STRUCTURE_MODE_FIELDS.has(fld))
    return { badge: "struktur", inferred: false, why: "Feste Struktur-/Modus-Wahl, nicht optimiert" };
  const cur = form ? form[fld] : undefined;
  const seedV = seedForm ? seedForm[fld] : undefined;
  if (seedV !== undefined && _eq(cur, seedV))
    return { badge: "seed", inferred: false, why: "Wert identisch zum Seed (geerbt/konstant)" };
  return { badge: "manual", inferred: true, why: "weicht vom Seed ab — manuell ODER vom Optimizer appliziert (nicht unterscheidbar)" };
}

/**
 * Range-Familie (Parameter-Ranges-Sektion) → Herkunft.
 * @param family z.B. "tp1_rr" | "sl_atr_mult" | "entry_swing_choices"
 * @param kind "numeric" (prüft {family}_min/_max) | "categorical" (prüft {family})
 * @param ranges aktuelle goals.ranges, seedRanges Seed-goals.ranges
 * @param tunableRangeKeys Set der range_keys aus /optimizer/tunables (für OPTIMIZER-DEFAULT)
 */
export function classifyRangeFamily(family, kind, ranges, seedRanges, tunableRangeKeys) {
  const presentKey = kind === "numeric" ? `${family}_min` : family;
  const v = ranges ? ranges[presentKey] : undefined;
  const isSet = v !== undefined && v !== null && !(Array.isArray(v) && v.length === 0);
  if (!isSet) {
    return (tunableRangeKeys && tunableRangeKeys.has(family))
      ? { badge: "optdefault", inferred: false, why: "vom Optimizer gesucht, aber im Artefakt NICHT gesetzt → Code-Default" }
      : { badge: "struktur", inferred: false, why: "keine Range gesetzt" };
  }
  const sr = seedRanges || {};
  const eq = kind === "numeric"
    ? (_eq(ranges[`${family}_min`], sr[`${family}_min`]) && _eq(ranges[`${family}_max`], sr[`${family}_max`]))
    : _eq(ranges[family], sr[family]);
  if (DERIVED_RANGE_FAMILIES.has(family))
    return eq ? { badge: "seed", inferred: false, why: "noch == Seed (Messung nicht (neu) abgeleitet)" }
              : { badge: "derived", inferred: true, why: "aus der Messung abgeleitet (weicht vom Seed ab) — inferiert" };
  return eq ? { badge: "seed", inferred: false, why: "Seed-Default (bewusst konstant)" }
            : { badge: "manual", inferred: true, why: "Range weicht vom Seed ab — manuell editiert (inferiert)" };
}
